import { useEffect } from "react";

/**
 * useCineReveal — global IntersectionObserver that toggles the .is-in class
 * on any element that uses .cine-reveal or .cine-stagger.
 *
 * Mount once at the top of <App /> so all reveal classes activate once they
 * scroll into view. No per-component setup required.
 */
export default function useCineReveal() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduceMotion) {
      document.querySelectorAll(".cine-reveal, .cine-stagger").forEach((el) => el.classList.add("is-in"));
      return;
    }

    const seen = new WeakSet();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !seen.has(entry.target)) {
            seen.add(entry.target);
            entry.target.classList.add("is-in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -8% 0px" }
    );

    const scan = () => {
      document.querySelectorAll(".cine-reveal:not(.is-in), .cine-stagger:not(.is-in)").forEach((el) => {
        if (!seen.has(el)) io.observe(el);
      });
    };

    scan();

    // Re-scan on route changes / dynamic content
    const mo = new MutationObserver(() => scan());
    mo.observe(document.body, { subtree: true, childList: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);
}
